import React from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ProductLanguageChipsProps {
  languages?: string[];
  baseLanguage?: string;
  maxVisible?: number;
}

const normalizeLanguage = (language?: string) => (language || '').trim().toLowerCase();

const normalizeLanguages = (languages?: string[], baseLanguage?: string) => {
  const normalizedBaseLanguage = normalizeLanguage(baseLanguage);
  const remainingLanguages = Array.from(
    new Set(
      (languages || [])
        .map(normalizeLanguage)
        .filter((language) => language && language !== normalizedBaseLanguage),
    ),
  ).sort();

  return normalizedBaseLanguage ? [normalizedBaseLanguage, ...remainingLanguages] : remainingLanguages;
};

export const ProductLanguageChips: React.FC<ProductLanguageChipsProps> = ({ languages, baseLanguage, maxVisible = 5 }) => {
  const { t } = useTranslation();
  const normalizedBaseLanguage = normalizeLanguage(baseLanguage);
  const normalizedLanguages = normalizeLanguages(languages, baseLanguage);
  const visibleLanguages = normalizedLanguages.slice(0, maxVisible);
  const hiddenLanguages = normalizedLanguages.slice(maxVisible);

  if (normalizedLanguages.length === 0) {
    return null;
  }

  const getLanguageLabel = (language: string) =>
    t(`languages.${language}`, { defaultValue: language.toUpperCase() });

  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {visibleLanguages.map((language) => (
        <Tooltip
          key={language}
          title={
            language === normalizedBaseLanguage
              ? t('products.baseLanguageTooltip', { language: getLanguageLabel(language) })
              : getLanguageLabel(language)
          }
        >
          <Chip
            label={language.toUpperCase()}
            size="small"
            sx={{
              height: 22,
              borderRadius: '999px',
              bgcolor: language === normalizedBaseLanguage ? '#edf7ef' : '#f1f5f2',
              color: language === normalizedBaseLanguage ? 'primary.dark' : 'text.secondary',
              border: '1px solid',
              borderColor: language === normalizedBaseLanguage ? 'primary.light' : 'divider',
              fontSize: '0.72rem',
              fontWeight: 700,
            }}
          />
        </Tooltip>
      ))}
      {hiddenLanguages.length > 0 && (
        <Tooltip title={hiddenLanguages.map(getLanguageLabel).join(', ')}>
          <Chip
            label={`+${hiddenLanguages.length}`}
            size="small"
            sx={{
              height: 22,
              borderRadius: '999px',
              bgcolor: 'grey.100',
              color: 'text.secondary',
              fontSize: '0.72rem',
              fontWeight: 700,
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};
